import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { MAX_CONTENT_WIDTH } from '../constants/layout';
import { getProjectSlug } from '../utils/slugify';
import Loading from './Loading';
import FadeIn from '../components/FadeIn';

export default function FAQ() {
  const { id: slug } = useParams();
  const [project, setProject] = useState(null);
  const [faqCategories, setFaqCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function fetchFAQs() {
      try {
        // First, fetch all projects to find the one matching the slug
        const projectsSnap = await getDocs(collection(db, 'development'));

        if (!isMounted) return;

        // Find project by slug
        const matchingProject = projectsSnap.docs.find(doc => {
          const projectData = doc.data();
          return getProjectSlug({ name: projectData.name }) === slug;
        });

        if (!matchingProject) {
          setProject(null);
          setLoading(false);
          return;
        }

        const projectId = matchingProject.id;
        const projectData = { id: projectId, ...matchingProject.data() };
        setProject(projectData);

        // Fetch FAQ categories
        const faqsRef = collection(db, 'development', projectId, 'faqs');
        const faqsSnap = await getDocs(faqsRef);

        if (!isMounted) return;

        // Fetch questions for each category
        const categoriesWithQuestions = await Promise.all(
          faqsSnap.docs.map(async (faqDoc) => {
            const categoryData = { id: faqDoc.id, ...faqDoc.data() };

            // Fetch questions subcollection
            const questionsRef = collection(db, 'development', projectId, 'faqs', faqDoc.id, 'questions');
            const questionsSnap = await getDocs(questionsRef);

            const questions = questionsSnap.docs.map(qDoc => ({
              id: qDoc.id,
              ...qDoc.data()
            }));

            return {
              ...categoryData,
              questions: questions.sort((a, b) => (a.order || 0) - (b.order || 0))
            };
          })
        );

        // Sort categories by order
        const sortedCategories = categoriesWithQuestions.sort(
          (a, b) => (a.order || 0) - (b.order || 0)
        );

        setFaqCategories(sortedCategories);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
        if (isMounted) {
          setProject(null);
          setFaqCategories([]);
          setLoading(false);
        }
      }
    }

    fetchFAQs();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 200px)',
        padding: '48px 16px',
      }}>
        <Loading />
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{
        maxWidth: MAX_CONTENT_WIDTH,
        margin: '0 auto',
        padding: '48px 16px',
        textAlign: 'center',
      }}>
        <p style={{ color: 'var(--color-text-secondary)' }}>Project not found.</p>
      </div>
    );
  }

  if (faqCategories.length === 0) {
    return (
      <div style={{
        maxWidth: MAX_CONTENT_WIDTH,
        margin: '0 auto',
        padding: '48px 16px',
        textAlign: 'center',
      }}>
        <Link
          to={`/${slug}`}
          style={{
            color: 'var(--color-link)',
            textDecoration: 'none',
            fontSize: '0.875rem',
            display: 'inline-block',
            marginBottom: '32px',
          }}
        >
          ← Back to {project.name}
        </Link>
        <p style={{ color: 'var(--color-text-secondary)' }}>No FAQs available.</p>
      </div>
    );
  }

  return (
    <FadeIn>
    <div style={{
      maxWidth: '900px',
      margin: '0 auto',
      padding: '48px 16px',
    }}>
      <Link
        to={`/${getProjectSlug(project)}`}
        style={{
          color: 'var(--color-link)',
          textDecoration: 'none',
          fontSize: '0.875rem',
          display: 'inline-block',
          marginBottom: '32px',
        }}
      >
        ← Back to {project.name}
      </Link>

      <h1 style={{
        fontSize: '2.5rem',
        fontWeight: 900,
        fontFamily: 'Helvetica, Arial, sans-serif',
        margin: '64px 0 24px 0',
        color: 'var(--color-text-primary)',
        textAlign: 'center',
      }}>
        Get Support
      </h1>

      <p style={{
        fontSize: '1rem',
        color: 'var(--color-text-secondary)',
        marginBottom: searchQuery.trim() ? '0' : '48px',
        textAlign: 'center',
        maxWidth: '700px',
        marginLeft: 'auto',
        marginRight: 'auto',
        maxHeight: searchQuery.trim() ? '0' : '200px',
        opacity: searchQuery.trim() ? 0 : 1,
        overflow: 'hidden',
        transition: 'max-height 0.3s ease, opacity 0.3s ease, margin-bottom 0.3s ease',
      }}>
        This is where you can ask questions and request support for {project.name} regarding issues, feedback, and feature requests. You can reach out by filling a form in Contact, {project.name === 'Shot Lists' ? 'sending a direct message on Instagram, or ' : ''}emailing the developer, Davin, at{' '}
        <a
          href="mailto:support@davinese.com"
          style={{
            color: 'var(--color-link)',
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = 'none';
          }}
        >
          support@davinese.com
        </a>
        .
      </p>

      {/* Contact Buttons */}
      <div style={{
        display: 'flex',
        gap: '16px',
        justifyContent: 'center',
        marginBottom: searchQuery.trim() ? '0' : '96px',
        flexWrap: 'wrap',
        maxHeight: searchQuery.trim() ? '0' : '200px',
        opacity: searchQuery.trim() ? 0 : 1,
        overflow: 'hidden',
        transition: 'max-height 0.3s ease, opacity 0.3s ease, margin-bottom 0.3s ease',
      }}>
        <Link
          to="/contact"
          style={{
            padding: '12px 24px',
            backgroundColor: 'var(--color-link)',
            color: '#ffffff',
            textDecoration: 'none',
            borderRadius: '0',
            fontSize: '1rem',
            fontWeight: 600,
            transition: 'opacity 0.2s ease',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.8';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"
              fill="currentColor"
            />
          </svg>
          Contact
        </Link>
        <a
          href="mailto:support@davinese.com"
          style={{
            padding: '12px 24px',
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-primary)',
            textDecoration: 'none',
            borderRadius: '0',
            fontSize: '1rem',
            fontWeight: 600,
            transition: 'background-color 0.2s ease',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-surface)';
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z"
              fill="currentColor"
            />
          </svg>
          Send Email
        </a>
        {project.name === 'Shot Lists' && (
          <a
            href="https://www.instagram.com/appsforfilmmaking"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '12px 24px',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-primary)',
              textDecoration: 'none',
              borderRadius: '0',
              fontSize: '1rem',
              fontWeight: 600,
              transition: 'background-color 0.2s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-surface)';
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ flexShrink: 0 }}
            >
              <path
                d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"
                fill="currentColor"
              />
            </svg>
            @appsforfilmmaking
          </a>
        )}
      </div>

      {/* Search Field */}
      <div style={{
        maxWidth: '600px',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: '48px',
      }}>
        <input
          type="text"
          placeholder="Search for help..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '16px 20px',
            fontSize: '1rem',
            border: '1px solid var(--color-border)',
            borderRadius: '0',
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text-primary)',
            outline: 'none',
            transition: 'border-color 0.2s ease',
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-link)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-border)';
          }}
        />
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}>
        {faqCategories
          .map((category) => {
            // Filter questions based on search query
            if (!searchQuery.trim()) {
              return category;
            }

            const query = searchQuery.toLowerCase();
            const filteredQuestions = category.questions.filter(q =>
              q.question.toLowerCase().includes(query) ||
              q.answer.toLowerCase().includes(query)
            );

            // Include category if title/subtitle matches or if it has matching questions
            const categoryMatches =
              category.title.toLowerCase().includes(query) ||
              (category.subtitle && category.subtitle.toLowerCase().includes(query));

            if (categoryMatches || filteredQuestions.length > 0) {
              return {
                ...category,
                questions: categoryMatches ? category.questions : filteredQuestions
              };
            }

            return null;
          })
          .filter(category => category !== null)
          .map((category) => (
          <div
            key={category.id}
            style={{
              border: '1px solid var(--color-border)',
              borderRadius: '0',
              overflow: 'hidden',
              backgroundColor: 'var(--color-surface)',
            }}
          >
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(category.id)}
              style={{
                width: '100%',
                padding: '24px',
                border: 'none',
                backgroundColor: expandedCategory === category.id
                  ? 'var(--color-surface-muted)'
                  : 'var(--color-surface)',
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (expandedCategory !== category.id) {
                  e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
                }
              }}
              onMouseLeave={(e) => {
                if (expandedCategory !== category.id) {
                  e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                }
              }}
            >
              <div>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  margin: '0 0 4px 0',
                  color: 'var(--color-text-primary)',
                }}>
                  {category.title}
                </h2>
                {category.subtitle && (
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                    margin: 0,
                  }}>
                    {category.subtitle}
                  </p>
                )}
              </div>
              <span style={{
                fontSize: '1.5rem',
                color: 'var(--color-text-secondary)',
                transition: 'transform 0.2s ease',
                transform: expandedCategory === category.id ? 'rotate(180deg)' : 'rotate(0deg)',
                display: 'inline-block',
              }}>
                ▼
              </span>
            </button>

            {/* Questions List */}
            {expandedCategory === category.id && (
              <div style={{
                borderTop: '1px solid var(--color-border)',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
              }}>
                {category.questions.map((question) => (
                  <div
                    key={question.id}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--color-text-primary)',
                      margin: 0,
                    }}>
                      {question.question}
                    </h3>
                    <div style={{
                      fontSize: '0.95rem',
                      lineHeight: 1.7,
                      color: 'var(--color-text-primary)',
                      whiteSpace: 'pre-wrap',
                    }}>
                      {question.answer}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Can't find what you're looking for section */}
      {searchQuery.trim() && (
        <div style={{
          marginTop: '48px',
          padding: '32px',
          textAlign: 'center',
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: '0',
        }}>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            margin: '0 0 16px 0',
          }}>
            Can't find what you're looking for?
          </h3>
          <p style={{
            fontSize: '1rem',
            color: 'var(--color-text-secondary)',
            marginBottom: '32px',
          }}>
            Contact the developer for assistance
          </p>
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            <Link
              to="/contact"
              style={{
                padding: '12px 24px',
                backgroundColor: 'var(--color-link)',
                color: '#ffffff',
                textDecoration: 'none',
                borderRadius: '0',
                fontSize: '1rem',
                fontWeight: 600,
                transition: 'opacity 0.2s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ flexShrink: 0 }}
              >
                <path
                  d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"
                  fill="currentColor"
                />
              </svg>
              Contact
            </Link>
            <a
              href="mailto:support@davinese.com"
              style={{
                padding: '12px 24px',
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                color: 'var(--color-text-primary)',
                textDecoration: 'none',
                borderRadius: '0',
                fontSize: '1rem',
                fontWeight: 600,
                transition: 'background-color 0.2s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-surface)';
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ flexShrink: 0 }}
              >
                <path
                  d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z"
                  fill="currentColor"
                />
              </svg>
              Send Email
            </a>
            {project.name === 'Shot Lists' && (
              <a
                href="https://www.instagram.com/appsforfilmmaking"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text-primary)',
                  textDecoration: 'none',
                  borderRadius: '0',
                  fontSize: '1rem',
                  fontWeight: 600,
                  transition: 'background-color 0.2s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <path
                    d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"
                    fill="currentColor"
                  />
                </svg>
                @appsforfilmmaking
              </a>
            )}
          </div>
        </div>
      )}
    </div>
    </FadeIn>
  );
}
